import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from './store';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LoginPage from './pages/auth/LoginPage';
// import RegisterPage from './pages/auth/RegisterPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import Layout from './components/layout/Layout';
import Anomalies from './pages/Anomalies';
import NouveauxAdherentsPage from './pages/nouveauxAdherents/NouveauxAdherentsPage';
import NafListsPage from './pages/NafListsPage';
import NotesPage from './pages/NotesPage';
import ProfilePage from './pages/profile/ProfilePage';
import Adherents from './pages/Adherents';
import ImportAdherents from './pages/ImportAdherents';
import AdherentDetail from './pages/AdherentDetail';
import AdherentEdit from './pages/AdherentEdit';
import AdherentCreate from './pages/AdherentCreate';
import AdminDashboard from './pages/admin/AdminDashboard';
import ProspectListsPage from './pages/ProspectListsPage';
import NotFound from './pages/NotFound';
import { useEffect } from 'react';
import { restoreSession } from './store/slices/authSlice';
import { AppDispatch } from './store';
import SentryErrorBoundary from './components/sentry/SentryErrorBoundary';
import { setSentryUser, clearSentryUser } from './utils/sentry';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { token } = useSelector((state: RootState) => state.auth);
  return token ? <Layout>{children}</Layout> : <Navigate to="/login" />;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { token, user } = useSelector((state: RootState) => state.auth);
  if (!token) return <Navigate to="/login" />;
  if (user?.role !== 'ROLE_ADMIN') return <Navigate to="/dashboard" />;
  return <Layout>{children}</Layout>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { token } = useSelector((state: RootState) => state.auth);
  return !token ? <>{children}</> : <Navigate to="/dashboard" />;
}

function App() {
  const dispatch = useDispatch<AppDispatch>();
  const { token, user } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (token) {
      dispatch(restoreSession());
    }
  }, [dispatch, token]);

  // Set user info for Sentry when authenticated
  useEffect(() => {
    if (token && user) {
      setSentryUser(String(user.id), user.role);
    } else {
      clearSentryUser();
    }
  }, [token, user]);

  return (
    <SentryErrorBoundary>
      <Router>
        <ToastContainer 
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss={false}
          draggable={true}
          draggablePercent={60}
          pauseOnHover={false}
          closeButton={true}
          limit={5}
          theme="light"
          containerId="main-toast-container"
        />
        <Routes>
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          {/* <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            }
          /> */}
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <DashboardPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/anomalies"
            element={
              <PrivateRoute>
                <Anomalies />
              </PrivateRoute>
            }
          />
          <Route
            path="/nouveaux-adherents"
            element={
              <PrivateRoute>
                <NouveauxAdherentsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/listes-naf"
            element={
              <PrivateRoute>
                <NafListsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/notes"
            element={
              <PrivateRoute>
                <NotesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/prospect-lists"
            element={
              <PrivateRoute>
                <ProspectListsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <ProfilePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/adherents"
            element={
              <PrivateRoute>
                <Adherents />
              </PrivateRoute>
            }
          />
          <Route
            path="/adherents/create"
            element={
              <PrivateRoute>
                <AdherentCreate />
              </PrivateRoute>
            }
          />
          <Route
            path="/adherents/:id"
            element={
              <PrivateRoute>
                <AdherentDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/adherents/:id/edit"
            element={
              <PrivateRoute>
                <AdherentEdit />
              </PrivateRoute>
            }
          />
          <Route
            path="/import-adherents"
            element={
              <PrivateRoute>
                <ImportAdherents />
              </PrivateRoute>
            }
          />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          {/* 404 Route - Must be last */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </SentryErrorBoundary>
  );
}

export default App;
